/**
 * `<ContactSteersSection>` — a contact's standing micro-instructions ("coach the
 * AI") on the contact detail page. These persist across every session with the
 * contact ("this VIP always gets extra care") and inject into the AI's system
 * prompt on every turn. Add / edit / remove; a session steer overrides one on
 * conflict. Gated on `conversation:monitor` (the tab is hidden otherwise).
 */

import { Button, Input, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useWorkspace } from "@cometchat-ai/auth/client";

import { Loader2, X } from "../icons";
import { formatApiError } from "../../lib/format-error";
import {
  type SessionSteer,
  contactSteerKeys,
  createContactSteer,
  deleteContactSteer,
  listContactSteers,
  updateContactSteer,
} from "../../handoff/api";

export function ContactSteersSection({ contactId }: { contactId: string }) {
  const { workspace } = useWorkspace();
  const qc = useQueryClient();
  const key = contactSteerKeys.list(workspace.slug, contactId);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);

  const query = useQuery({ queryKey: key, queryFn: () => listContactSteers(contactId) });
  const steers: SessionSteer[] = query.data?.items ?? [];

  const onErr = (err: unknown) =>
    toast.error("Something went wrong", { description: formatApiError(err) });

  const add = useMutation({
    mutationFn: (text: string) => createContactSteer(contactId, { text }),
    onSuccess: () => {
      setDraft("");
      void invalidate();
    },
    onError: onErr,
  });
  const edit = useMutation({
    mutationFn: (v: { id: string; text: string }) => updateContactSteer(contactId, v.id, v.text),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
    },
    onError: onErr,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteContactSteer(contactId, id),
    onSuccess: () => void invalidate(),
    onError: onErr,
  });

  return (
    <div className="max-w-2xl space-y-4">
      <p className="text-[13px] text-muted-foreground">
        Standing instructions the AI must follow in <strong>every</strong> conversation with this
        customer. They override the knowledge base and tools; a live instruction on a specific chat
        can override them just for that chat.
      </p>

      <ul className="space-y-2">
        {steers.map((s) => (
          <li
            key={s.id}
            className="flex items-start gap-2 rounded-lg border border-border px-3 py-2"
            data-testid={`contact-steer-${s.id}`}
          >
            {editing?.id === s.id ? (
              <>
                <Input
                  autoFocus
                  value={editing.text}
                  onChange={(e) => setEditing({ id: s.id, text: e.target.value })}
                  className="h-8 flex-1 rounded-md text-[13px] md:text-[13px]"
                />
                <Button
                  size="sm"
                  className="h-8 rounded-md text-[12px]"
                  disabled={edit.isPending || !editing.text.trim()}
                  onClick={() => edit.mutate({ id: s.id, text: editing.text.trim() })}
                >
                  Save
                </Button>
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <span className="min-w-0 flex-1 text-[13px] leading-snug text-foreground">
                  {s.text}
                  {s.agentId ? (
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      one agent
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  onClick={() => setEditing({ id: s.id, text: s.text })}
                  className="text-[12px] text-muted-foreground hover:text-foreground"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => remove.mutate(s.id)}
                  aria-label="Remove"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </li>
        ))}
        {steers.length === 0 && !query.isPending ? (
          <li className="text-[13px] text-muted-foreground">No standing instructions yet.</li>
        ) : null}
      </ul>

      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) add.mutate(draft.trim());
          }}
          placeholder="e.g. Treat as VIP — be extra accommodating, offer a goodwill perk"
          className="h-9 flex-1 rounded-md text-[13px] md:text-[13px]"
          data-testid="contact-steer-add-input"
        />
        <Button
          size="sm"
          className="h-9 rounded-md text-[13px]"
          disabled={add.isPending || !draft.trim()}
          onClick={() => add.mutate(draft.trim())}
          data-testid="contact-steer-add"
        >
          {add.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Add
        </Button>
      </div>
    </div>
  );
}
